import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import API from "../services/api"
import BottomNav from "../components/BottomNav"
import { getUser } from "../utils/storage"

const MyContests = () => {
  const navigate = useNavigate()
  const [contests, setContests] = useState([])
  const [playerChallenges, setPlayerChallenges] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState("teams")

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const user = getUser()
      const id = user.id || 1
      const [contestsRes, challengesRes] = await Promise.all([
        API.get(`/contests/my-contests?userId=${id}`),
        API.get(`/challenges/my/${id}`)
      ])
      
      if (contestsRes.data) setContests(contestsRes.data)
      if (challengesRes.data?.entries) setPlayerChallenges(challengesRes.data.entries)
    } catch (err) {
      console.log(err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="bg-theme-bg min-h-screen text-theme-text pb-20 font-sans">
      <div className="p-4 bg-theme-card border-b border-theme-border flex items-center sticky top-0 z-50 shadow-theme-sm">
        <button 
          onClick={() => navigate("/")} 
          className="w-10 h-10 flex items-center justify-center bg-theme-bg rounded-xl border border-theme-border text-theme-text transition-all active:scale-95 shadow-theme-sm"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <h2 className="font-bold text-lg text-theme-text tracking-tight uppercase flex-1 text-center pr-10">My Gameplays</h2>
      </div>

      <div className="flex justify-center bg-theme-card border-b border-theme-border p-3 sticky top-[73px] z-10">
        <div className="bg-theme-bg rounded-xl p-1 flex w-full border border-theme-border">
          <button onClick={() => navigate("/my-teams", { replace: true })} className="flex-1 py-2 text-[10px] font-bold uppercase tracking-wider rounded-lg text-theme-text-sec transition-all">Teams</button>
          <button onClick={() => setActiveTab("teams")} className={`flex-1 py-2 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all ${activeTab === "teams" ? "bg-accent text-white shadow-sm" : "text-theme-text-sec opacity-60"}`}>Contests</button>
          <button onClick={() => setActiveTab("players")} className={`flex-1 py-2 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all ${activeTab === "players" ? "bg-accent text-white shadow-sm" : "text-theme-text-sec opacity-60"}`}>Players</button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-32 gap-4">
            <div className="w-9 h-9 border-2 border-accent border-t-transparent rounded-full animate-spin"></div>
            <p className="text-[10px] font-bold text-theme-text-sec uppercase tracking-widest">Loading...</p>
        </div>
      ) : activeTab === "teams" ? (
        contests.length === 0 ? (
          <div className="text-center py-20 px-10">
            <div className="w-24 h-24 bg-theme-card rounded-full flex items-center justify-center text-4xl mb-8 shadow-theme-sm border border-theme-border mx-auto opacity-30">🎫</div>
            <h3 className="text-base font-bold uppercase text-theme-text tracking-widest mb-3 opacity-80">No Contests</h3>
            <p className="text-theme-text-sec text-[10px] font-bold uppercase tracking-wider mb-10 leading-relaxed opacity-60">You haven't entered any fantasy contests yet.</p>
            <button onClick={() => navigate("/")} className="bg-accent text-white px-8 py-3.5 rounded-xl font-bold text-[10px] uppercase tracking-widest shadow-sm transition active:scale-95">Browse Matches</button>
          </div>
        ) : (
          <div className="p-4 space-y-4 max-w-md mx-auto">
            {contests.map((contest, index) => (
              <div key={index} onClick={() => navigate(`/leaderboard/${contest.id}`)} className="bg-theme-card rounded-theme p-4 shadow-theme-sm border border-theme-border active:scale-[0.98] transition-all relative">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <p className="text-theme-text-sec text-[8px] font-bold uppercase tracking-widest opacity-60 mb-1">Prize Pool</p>
                    <p className="text-xl font-bold text-theme-text tracking-tight">₹{contest.first_prize}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-theme-text-sec text-[8px] font-bold uppercase tracking-widest opacity-60 mb-1">Entry Fee</p>
                    <p className="text-base font-bold text-accent">₹{contest.entry_fee}</p>
                  </div>
                </div>
                <div className="w-full bg-theme-bg border border-theme-border h-1.5 rounded-full mt-2 overflow-hidden">
                  <div className="bg-accent h-full transition-all duration-500" style={{ width: `${(contest.joined_spots / contest.total_spots) * 100}%` }} />
                </div>
                <div className="flex justify-between text-[9px] text-theme-text-sec mt-2 font-bold uppercase tracking-wider opacity-60">
                  <span>{contest.joined_spots} Joined</span><span>{contest.total_spots - contest.joined_spots} Left</span>
                </div>
                <div className="mt-5 pt-4 border-t border-theme-border flex justify-between items-center">
                  <span className="bg-theme-bg border border-theme-border px-2.5 py-1.5 rounded-lg text-[9px] font-bold text-theme-text uppercase tracking-tight">Team: {contest.team_id}</span>
                  <span className="text-accent text-[9px] font-bold uppercase tracking-widest">Leaderboard &rarr;</span>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        playerChallenges.length === 0 ? (
          <div className="text-center py-20 px-10">
            <div className="w-24 h-24 bg-theme-card rounded-full flex items-center justify-center text-4xl mb-8 shadow-theme-sm border border-theme-border mx-auto opacity-30">🏏</div>
            <h3 className="text-base font-bold uppercase text-theme-text tracking-widest mb-3 opacity-80">No Player Contests</h3>
            <p className="text-theme-text-sec text-[10px] font-bold uppercase tracking-wider mb-10 leading-relaxed opacity-60">Join player performance challenges to see them here.</p>
            <button onClick={() => navigate("/player-performance")} className="bg-accent text-white px-8 py-3.5 rounded-xl font-bold text-[10px] uppercase tracking-widest shadow-sm transition active:scale-95">Go to Player Contests</button>
          </div>
        ) : (
          <div className="p-4 space-y-4 max-w-md mx-auto">
            {playerChallenges.map((challenge, index) => (
              <div key={index} onClick={() => navigate(`/player-leaderboard/${challenge.challenge_id}`)} className="bg-theme-card rounded-theme p-4 shadow-theme-sm border border-theme-border active:scale-[0.98] transition-all relative">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-[8px] font-bold px-2 py-1 rounded bg-accent/10 text-accent border border-accent/20 tracking-widest uppercase">{challenge.category.replace('_', ' ')}</span>
                  <span className="text-[9px] text-theme-text-sec font-bold uppercase tracking-widest opacity-60">Entry ₹{challenge.entry_fee}</span>
                </div>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-theme-bg border border-theme-border rounded-full flex items-center justify-center p-1 shadow-theme-sm">
                    <img src="/player_placeholder.png" alt="Player" className="w-full h-full object-contain" />
                  </div>
                  <div>
                    <p className="font-bold text-theme-text text-base tracking-tight uppercase">{challenge.player_name}</p>
                    <p className="text-[9px] text-theme-text-sec uppercase font-bold tracking-widest opacity-40">Match: {challenge.match_id}</p>
                  </div>
                </div>
                <div className="flex justify-between items-center border-t border-theme-border pt-4 mt-1">
                  <div className="flex flex-col">
                    <p className="text-[8px] text-theme-text-sec font-bold uppercase tracking-widest opacity-60">Prize Pool</p>
                    <p className="text-sm font-bold text-accent">₹{challenge.prize_pool}</p>
                  </div>
                  <span className="text-[9px] font-bold text-theme-text-sec uppercase tracking-widest flex items-center gap-1.5 px-3 py-1.5 bg-theme-bg border border-theme-border rounded-lg">View <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14m-7-7l7 7-7 7"/></svg></span>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      <BottomNav activeTab="my-teams" />
    </div>
  )
}

export default MyContests
